// pages/entry-form/BulkSellingFormDialog.tsx
import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Dialog, DialogContent, DialogActions,
  CircularProgress, Alert, Tooltip, alpha, Autocomplete, MenuItem, Select, Stack,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon, Close as CloseIcon, Delete as DeleteIcon,
  Person as PersonIcon, TrendingUp as SaleIcon,
} from '@mui/icons-material';
import {
  tradesService, Product, SellerInfo, PurchasePriceList, SellingBulkItemPayload,
} from '../../services/tradesService';

const ACCENT     = '#4CAF50';
const CASH_PAID  = 'Cash Paid';
const SELL_TYPES = ['Retail', 'Wholesale', CASH_PAID];

type FieldKey =
  | 'sale_date' | 'sell_type' | 'product' | 'purchase_price' | 'quantity_pcs'
  | 'sell_rate' | 'discount_amount' | 'cash_amount' | 'remarks';
type RowErrors = Partial<Record<FieldKey, string>>;
type NumField  = 'quantity_pcs' | 'sell_rate' | 'discount_amount' | 'cash_amount';

const FIELD_KEYS: FieldKey[] = [
  'sale_date', 'sell_type', 'product', 'purchase_price', 'quantity_pcs',
  'sell_rate', 'discount_amount', 'cash_amount', 'remarks',
];

interface Row {
  key: string;
  sale_date: string;
  sell_type: string;
  product: Product | null;
  purchase_price: PurchasePriceList | null;
  quantity_pcs: string;
  sell_rate: string;
  discount_amount: string;
  cash_amount: string;
  remarks: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const num = (v: string | number | null | undefined) => {
  const n = parseFloat(String(v ?? ''));
  return isNaN(n) ? 0 : n;
};
const r2  = (n: number) => Math.round(n * 100) / 100;   // DRF rejects >2 decimals
const fmt = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 2 });

let keySeq = 0;
const newRow = (prev?: Row): Row => ({
  key: `row-${Date.now()}-${keySeq++}`,
  sale_date: prev?.sale_date ?? localToday(),                       // carry over from last row
  sell_type: prev && prev.sell_type !== CASH_PAID ? prev.sell_type : 'Retail',
  product: null, purchase_price: null,
  quantity_pcs: '', sell_rate: '', discount_amount: '0', cash_amount: '', remarks: '',
});

const rowTotal = (r: Row) =>
  r.sell_type === CASH_PAID
    ? 0
    : r2(Math.max(0, num(r.quantity_pcs) * num(r.sell_rate) - num(r.discount_amount)));

const rowDue = (r: Row) =>
  r.sell_type === CASH_PAID
    ? r2(-num(r.cash_amount))
    : r2(Math.max(0, rowTotal(r) - num(r.cash_amount)));

const COLS: { label: string; w: number; align?: 'right' | 'center' }[] = [
  { label: '#',                w: 36, align: 'center' },
  { label: 'Sale Date *',      w: 150 },
  { label: 'Sell Type *',      w: 130 },
  { label: 'Product *',        w: 400 },
  { label: 'Purchase Price *', w: 150, },
  { label: 'Remaining',        w: 90,  align: 'right' },
  { label: 'Qty (Pcs) *',      w: 100, align: 'center' },
  { label: 'Sell Rate *',      w: 115, },
  { label: 'Discount',         w: 100, },
  { label: 'Total Sales',      w: 110, align: 'right' },
  { label: 'Cash',             w: 115, align: 'center' },
  { label: 'Due',              w: 100, align: 'right' },
  { label: 'Remarks',          w: 170, align: 'center' },
  { label: '',                 w: 44 },
];
const TABLE_MIN_W = COLS.reduce((s, c) => s + c.w, 0);

const cellSx = { py: 1, px: 0.75, verticalAlign: 'top' as const };
const inSx = {
  '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.85rem' },
  '& .MuiFormHelperText-root': { mx: 0.5, fontSize: '0.7rem' },
};

// ── Component ─────────────────────────────────────────────────────────────────
interface Props {
  open: boolean;
  products: Product[];
  sellers: SellerInfo[];
  onClose: () => void;
  onSaved: () => void;
}

const BulkSellingFormDialog: React.FC<Props> = ({ open, products, sellers, onClose, onSaved }) => {
  const [seller, setSeller]             = useState<SellerInfo | null>(null);
  const [sellerError, setSellerError]   = useState('');
  const [rows, setRows]                 = useState<Row[]>([newRow()]);
  const [errors, setErrors]             = useState<Record<string, RowErrors>>({});
  const [priceLists, setPriceLists]     = useState<Record<number, PurchasePriceList[]>>({});
  const [priceLoading, setPriceLoading] = useState<Record<number, boolean>>({});
  const [apiError, setApiError]         = useState('');
  const [saving, setSaving]             = useState(false);
  const requested = useRef<Set<number>>(new Set());   // product_ids already fetched

  const cashPaidProduct = products.find(p => p.product_name === CASH_PAID) ?? null;
  const productOptions  = products.filter(p => p.is_active && p.product_name !== CASH_PAID);

  // ── Reset on open ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setSeller(null); setSellerError('');
    setRows([newRow()]); setErrors({});
    setPriceLists({}); setPriceLoading({});
    setApiError(''); setSaving(false);
    requested.current.clear();
  }, [open]);

  // ── Price list per product (shared by all rows of that product) ─────────────
  const loadPrices = (productId: number) => {
    if (requested.current.has(productId)) return;
    requested.current.add(productId);
    setPriceLoading(s => ({ ...s, [productId]: true }));
    tradesService
      .listPurchasePrices({ product_id: productId, with_stock: true })
      .then(res => {
        const list = res.results;
        setPriceLists(s => ({ ...s, [productId]: list }));
        if (list.length === 1) {
          setRows(rs => rs.map(r =>
            r.product?.product_id === productId && !r.purchase_price
              ? { ...r, purchase_price: list[0] } : r));
        }
      })
      .catch(() => { requested.current.delete(productId); })   // allow retry
      .finally(() => setPriceLoading(s => ({ ...s, [productId]: false })));
  };

  // ── Row mutations ───────────────────────────────────────────────────────────
  const updateRow = (key: string, patch: Partial<Row>) => {
    setRows(rs => rs.map(r => (r.key === key ? { ...r, ...patch } : r)));
    setErrors(es => {
      if (!es[key]) return es;
      const next = { ...es[key] };
      (Object.keys(patch) as FieldKey[]).forEach(f => delete next[f]);
      return { ...es, [key]: next };
    });
  };

  const changeSellType = (row: Row, value: string) => {
    if (value === CASH_PAID) {
      updateRow(row.key, {
        sell_type: value, product: cashPaidProduct, purchase_price: null,
        quantity_pcs: '1', sell_rate: '0', discount_amount: '0',
      });
    } else if (row.sell_type === CASH_PAID) {
      updateRow(row.key, {
        sell_type: value, product: null, purchase_price: null,
        quantity_pcs: '', sell_rate: '', discount_amount: '0',
      });
    } else {
      updateRow(row.key, { sell_type: value });
    }
  };

  const changeProduct = (row: Row, product: Product | null) => {
    const cached = product ? priceLists[product.product_id] : undefined;
    updateRow(row.key, { product, purchase_price: cached?.length === 1 ? cached[0] : null });
    if (product) loadPrices(product.product_id);
  };

  const addRow = () => setRows(rs => [...rs, newRow(rs[rs.length - 1])]);

  const removeRow = (key: string) => {
    setRows(rs => (rs.length > 1 ? rs.filter(r => r.key !== key) : rs));
    setErrors(es => { const n = { ...es }; delete n[key]; return n; });
  };

  // Stock available to this row = batch remaining − qty taken by rows ABOVE it on the same batch
  const availableFor = (row: Row): number | null => {
    const pp = row.purchase_price;
    if (!pp) return null;
    const idx = rows.findIndex(r => r.key === row.key);
    const usedAbove = rows.slice(0, idx).reduce((sum, r) =>
      r.purchase_price?.purchase_price_id === pp.purchase_price_id
        ? sum + (parseInt(r.quantity_pcs) || 0) : sum, 0);
    return pp.remaining_quantity - usedAbove;
  };

  // Running balance shown in the grid = stock left after this row
  const remainingAfter = (row: Row): number | null => {
    const av = availableFor(row);
    return av === null ? null : av - (parseInt(row.quantity_pcs) || 0);
  };

  // ── Validation ──────────────────────────────────────────────────────────────
  const validate = () => {
    const errs: Record<string, RowErrors> = {};
    const max = localToday();
    rows.forEach(r => {
      const e: RowErrors = {};
      const cp = r.sell_type === CASH_PAID;

      if (!r.sale_date) e.sale_date = 'Required';
      else if (r.sale_date > max) e.sale_date = 'Future date';

      if (!r.product) e.product = cp ? '"Cash Paid" product not found' : 'Required';

      if (!cp) {
        const q    = parseInt(r.quantity_pcs) || 0;
        const rate = num(r.sell_rate);
        if (!r.purchase_price) e.purchase_price = 'Required';
        if (q <= 0) e.quantity_pcs = 'Must be > 0';
        else {
          const av = availableFor(r);
          if (av !== null && q > av) e.quantity_pcs = `Max ${Math.max(0, av)}`;
        }
        if (r.sell_rate === '') e.sell_rate = 'Required';
        else if (r.purchase_price && rate <= num(r.purchase_price.purchase_rate))
          e.sell_rate = `Must be > ${num(r.purchase_price.purchase_rate).toFixed(2)}`;
        const d = num(r.discount_amount);
        if (d < 0) e.discount_amount = 'Must be ≥ 0';
        else if (d > q * rate) e.discount_amount = 'Exceeds gross';
      }
      if (num(r.cash_amount) < 0) e.cash_amount = 'Must be ≥ 0';

      if (Object.keys(e).length) errs[r.key] = e;
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setApiError('');
    const sellerOk = !!seller;
    setSellerError(sellerOk ? '' : 'Seller is required');
    const rowsOk = validate();
    if (!sellerOk || !rowsOk) {
      setApiError('Fix the highlighted fields, then save again.');
      return;
    }

    const items: SellingBulkItemPayload[] = rows.map(r => ({
      sale_date:         r.sale_date,
      sell_type:         r.sell_type,
      product:           r.product!.product_id,
      purchase_price:    r.purchase_price?.purchase_price_id ?? null,
      quantity_pcs:      parseInt(r.quantity_pcs),
      sell_rate:         r2(num(r.sell_rate)),
      discount_amount:   r2(num(r.discount_amount)),
      total_sales_price: rowTotal(r),
      cash_amount:       r2(num(r.cash_amount)),
      due_amount:        rowDue(r),
      remarks:           r.remarks.trim(),
    }));

    setSaving(true);
    try {
      await tradesService.createSalesBulk({ seller_info: seller!.seller_info_id, items });
      onSaved(); onClose();
    } catch (e: any) {
      const data     = e?.data;
      const itemErrs = data?.errors?.items as Record<string, Record<string, string[] | string>> | undefined;
      if (itemErrs) {
        const mapped: Record<string, RowErrors> = {};
        const lines: string[] = [];
        Object.entries(itemErrs).forEach(([idx, fieldErrs]) => {
          const row = rows[Number(idx)];
          if (!row) return;
          const re: RowErrors = {};
          Object.entries(fieldErrs).forEach(([field, msgs]) => {
            const msg = Array.isArray(msgs) ? String(msgs[0]) : String(msgs);
            if ((FIELD_KEYS as string[]).includes(field)) re[field as FieldKey] = msg;
            lines.push(`Row ${Number(idx) + 1}: ${msg}`);
          });
          mapped[row.key] = re;
        });
        setErrors(mapped);
        setApiError(lines.join('  •  ') || data?.message || 'Some rows have errors.');
      } else {
        setApiError(data?.errors?.seller_info?.[0] || data?.message || 'Something went wrong.');
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Totals ──────────────────────────────────────────────────────────────────
  const grand = rows.reduce(
    (a, r) => ({ total: a.total + rowTotal(r), cash: a.cash + num(r.cash_amount), due: a.due + rowDue(r) }),
    { total: 0, cash: 0, due: 0 },
  );

  // ── Small render helpers ────────────────────────────────────────────────────
  const numInput = (r: Row, field: NumField, e: RowErrors, opts?: { disabled?: boolean; placeholder?: string; step?: string }) => (
    <TextField
      type="number" size="small" fullWidth
      value={r[field]}
      disabled={opts?.disabled}
      placeholder={opts?.placeholder}
      onChange={ev => updateRow(r.key, { [field]: ev.target.value } as Partial<Row>)}
      onWheel={ev => (ev.target as HTMLInputElement).blur()}
      inputProps={{ step: opts?.step ?? '0.01', min: 0 }}
      error={!!e[field]} helperText={e[field]}
      sx={inSx}
    />
  );

  const readOnlyValue = (text: string, color?: string) => (
    <Typography variant="body2" fontWeight={700} sx={{ pt: 1, color: color ?? 'text.primary', textAlign: 'right' }}>
      {text}
    </Typography>
  );

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xl" fullWidth
      PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden', height: '90vh', display: 'flex', flexDirection: 'column' } }}>

      {/* Header */}
      <Box sx={{
        background: `linear-gradient(135deg, ${ACCENT} 0%, #2E7D32 100%)`,
        px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SaleIcon sx={{ color: 'white', fontSize: 22 }} />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>
            New Sale Entry
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={saving} sx={{ color: 'white', p: 0.5 }}><CloseIcon /></IconButton>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}>
        {apiError && <Alert severity="error" sx={{ borderRadius: 2 }}>{apiError}</Alert>}

        {/* Seller — entered once */}
        <Autocomplete
          options={sellers}
          value={seller}
          getOptionLabel={o => `${o.seller_name} (${o.mobile})`}
          isOptionEqualToValue={(o, v) => o.seller_info_id === v.seller_info_id}
          onChange={(_, v) => { setSeller(v); if (v) setSellerError(''); }}
          sx={{ maxWidth: 460 }}
          renderInput={params => (
            <TextField {...params} label="Seller *"
              error={!!sellerError} helperText={sellerError}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <PersonIcon fontSize="small" sx={{ color: ACCENT }} />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
          )} />

        {/* Line items */}
        <TableContainer sx={{
          flex: 1, minHeight: 0, overflow: 'auto', borderRadius: 2,
          border: `1px solid ${alpha(ACCENT, 0.15)}`,
        }}>
          <Table size="small" stickyHeader sx={{ minWidth: TABLE_MIN_W, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                {COLS.map(c => (
                  <TableCell key={c.label || 'actions'} align={c.align} sx={{
                    width: c.w, fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary',
                    whiteSpace: 'nowrap', px: 0.75, background: theme => theme.palette.background.paper,
                    borderBottom: `2px solid ${alpha(ACCENT, 0.15)}`,
                  }}>
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, idx) => {
                const e        = errors[r.key] ?? {};
                const cp       = r.sell_type === CASH_PAID;
                const pid      = r.product?.product_id;
                const list     = pid ? priceLists[pid] ?? [] : [];
                const loadingP = pid ? !!priceLoading[pid] : false;
                const avail    = remainingAfter(r);
                const availColor =
                  avail === null ? 'text.disabled' : avail < 0 ? '#f44336' : avail <= 10 ? '#FF9800' : ACCENT;
                const minRate  = r.purchase_price ? num(r.purchase_price.purchase_rate) : null;

                return (
                  <TableRow key={r.key} sx={{ '&:hover': { background: alpha(ACCENT, 0.03) } }}>
                    <TableCell align="center" sx={cellSx}>
                      <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>{idx + 1}</Typography>
                    </TableCell>

                    <TableCell sx={cellSx}>
                      <TextField type="date" size="small" fullWidth value={r.sale_date}
                        onChange={ev => updateRow(r.key, { sale_date: ev.target.value })}
                        inputProps={{ max: localToday() }}
                        error={!!e.sale_date} helperText={e.sale_date} sx={inSx} />
                    </TableCell>

                    <TableCell sx={cellSx}>
                      <Select size="small" fullWidth value={r.sell_type}
                        onChange={ev => changeSellType(r, ev.target.value)}
                        sx={{ borderRadius: 1.5, fontSize: '0.85rem' }}>
                        {SELL_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                      </Select>
                    </TableCell>

                    <TableCell sx={cellSx}>
                      <Autocomplete size="small"
                        options={cp && cashPaidProduct ? [cashPaidProduct] : productOptions}
                        value={r.product}
                        disabled={cp}
                        getOptionLabel={o => `${o.product_name} (${o.unit_of_measure})`}
                        isOptionEqualToValue={(o, v) => o.product_id === v.product_id}
                        onChange={(_, v) => changeProduct(r, v)}
                        renderInput={p => (
                          <TextField {...p} placeholder="Select product"
                            error={!!e.product} helperText={e.product} sx={inSx} />
                        )} />
                    </TableCell>

                    <TableCell sx={cellSx}>
                      {cp ? readOnlyValue('—', 'text.disabled') : (
                        <Autocomplete size="small"
                          options={list}
                          value={r.purchase_price}
                          loading={loadingP}
                          disabled={!r.product || loadingP}
                          getOptionLabel={o => `Tk. ${num(o.purchase_rate).toFixed(2)}`}
                          isOptionEqualToValue={(o, v) => o.purchase_price_id === v.purchase_price_id}
                          noOptionsText={!r.product ? 'Select a product first' : 'No stock available'}
                          onChange={(_, v) => updateRow(r.key, { purchase_price: v })}
                          renderInput={p => (
                            <TextField {...p} placeholder={loadingP ? 'Loading…' : 'Rate'}
                              error={!!e.purchase_price}
                              helperText={e.purchase_price ||
                                (r.product && !loadingP && requested.current.has(r.product.product_id) && list.length === 0
                                  ? 'No stock' : '')}
                              sx={inSx} />
                          )} />
                      )}
                    </TableCell>

                    <TableCell sx={cellSx}>
                      {readOnlyValue(cp || avail === null ? '—' : String(avail), availColor)}
                    </TableCell>

                    <TableCell sx={cellSx}>{numInput(r, 'quantity_pcs', e, { disabled: cp, step: '1' })}</TableCell>
                    <TableCell sx={cellSx}>
                      {numInput(r, 'sell_rate', e, {
                        disabled: cp,
                        placeholder: minRate !== null ? `> ${minRate.toFixed(2)}` : undefined,
                      })}
                    </TableCell>
                    <TableCell sx={cellSx}>{numInput(r, 'discount_amount', e, { disabled: cp })}</TableCell>

                    <TableCell sx={cellSx}>{readOnlyValue(fmt(rowTotal(r)), ACCENT)}</TableCell>

                    <TableCell sx={cellSx}>{numInput(r, 'cash_amount', e)}</TableCell>

                    <TableCell sx={cellSx}>{readOnlyValue(fmt(rowDue(r)), '#f44336')}</TableCell>

                    <TableCell sx={cellSx}>
                      <TextField size="small" fullWidth value={r.remarks}
                        onChange={ev => updateRow(r.key, { remarks: ev.target.value })} sx={inSx} />
                    </TableCell>

                    <TableCell align="center" sx={cellSx}>
                      <Tooltip title={rows.length === 1 ? 'At least one product is required' : 'Remove row'}>
                        <span>
                          <IconButton size="small" disabled={rows.length === 1}
                            onClick={() => removeRow(r.key)}
                            sx={{ color: '#f44336', mt: 0.25 }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box>
          <Button startIcon={<AddIcon />} onClick={addRow} variant="outlined" size="small"
            sx={{ borderRadius: 2, borderColor: alpha(ACCENT, 0.5), color: ACCENT }}>
            Add product
          </Button>
        </Box>
      </DialogContent>

      {/* Footer: grand totals + actions */}
      <DialogActions sx={{
        px: 3, py: 2, gap: 1, flexShrink: 0, borderTop: '1px solid', borderColor: 'divider',
        justifyContent: 'space-between', flexWrap: 'wrap',
      }}>
        <Stack direction="row" spacing={3} flexWrap="wrap">
          {[
            { label: 'Products',          value: String(rows.length),       color: 'text.primary' },
            { label: 'Grand Total Sales', value: `Tk. ${fmt(grand.total)}`, color: ACCENT },
            { label: 'Total Cash',              value: `Tk. ${fmt(grand.cash)}`,  color: '#FF9800' },
            { label: 'Total Due',               value: `Tk. ${fmt(grand.due)}`,   color: '#f44336' },
          ].map(s => (
            <Box key={s.label}>
              <Typography variant="caption" color="text.secondary" display="block">{s.label}</Typography>
              <Typography fontWeight={800} sx={{ color: s.color, fontSize: '1rem' }}>{s.value}</Typography>
            </Box>
          ))}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} disabled={saving} variant="outlined"
            sx={{ borderRadius: 2, borderColor: alpha(ACCENT, 0.4), color: ACCENT }}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ borderRadius: 2, background: `linear-gradient(135deg, ${ACCENT} 0%, #2E7D32 100%)` }}>
            {saving ? 'Saving...' : `Save ${rows.length} ${rows.length === 1 ? 'entry' : 'entries'}`}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default BulkSellingFormDialog;
